/**
 * bossDroneSpawner.ts
 * Helper module: spawn fighter drones from the Void Mothership boss
 * during HOLD. Sibling of `bossSystem.ts` — extracted to keep that
 * module within the 300-line file budget.
 *
 * Boss-spawned drones intentionally do NOT get their own lifecycle
 * list. Once acquired from the shared fighter pool they live on
 * `spaceState.enemies` and inherit `spaceEnemySystem`'s update,
 * collision, and reset paths. `resetSpaceEnemies()` already releases
 * them on scene teardown.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * @module bossDroneSpawner
 */

import { spaceState } from './spaceState';
import { VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { SPAWN_SPEC } from './spaceEnemyPools';
import {
  SPACE_ENEMY_MAX_ACTIVE,
  SPACE_ENEMY_SPAWN_Y_MIN,
  SPACE_ENEMY_SPAWN_Y_MAX_OFFSET,
} from '../../constants/spaceEnemyConstants';
import {
  VOID_MOTHERSHIP_DRONE_SPAWN_X_OFFSET,
  VOID_MOTHERSHIP_DRONE_SPAWN_Y_JITTER,
} from '../../constants/bossConstants';

/**
 * Injectable RNG for the drone-spawn Y jitter. Defaults to `Math.random`
 * so production code is non-deterministic; tests pin a sequence via
 * `setBossDroneRng` and restore it with `resetBossDroneRng`. The boss
 * spawn cadence itself is fully deterministic (driven by cooldowns), so
 * this RNG is intentionally narrow — it only chooses the spawn Y.
 *
 * @internal
 */
let droneRng: () => number = Math.random;

/**
 * Replace the drone-spawn RNG. Test-only seam.
 *
 * @param fn - Zero-arg function returning a number in `[0, 1)`.
 * @internal
 */
export function setBossDroneRng(fn: () => number): void {
  droneRng = fn;
}

/**
 * Restore the drone-spawn RNG to `Math.random`. Call from test teardown
 * so pinned sequences do not leak across suites.
 *
 * @internal
 */
export function resetBossDroneRng(): void {
  droneRng = Math.random;
}

/**
 * Spawn a single FighterDrone just left of the boss, jittered vertically
 * around the boss's centre. Returns `true` on a successful spawn,
 * `false` when:
 *   - `spaceState.boss` is null, OR
 *   - the global enemy budget (`SPACE_ENEMY_MAX_ACTIVE`) is saturated,
 *     OR
 *   - the fighter pool is exhausted.
 *
 * Callers (currently `updateBoss`) use the return value to gate the
 * drone-spawn cooldown so a pool/cap miss retries next tick rather than
 * burning a full cooldown.
 *
 * DECISION: Acquire via `SPAWN_SPEC.fighter.acquire()` rather than
 * importing the fighter pool directly so pool ownership stays in
 * `spaceEnemyPools` and this module doesn't grow a second per-kind
 * import surface as later boss phases add more spawn kinds.
 *
 * @returns `true` when a drone was spawned, `false` on a pool/cap miss.
 */
export function spawnBossDrone(): boolean {
  const boss = spaceState.boss;
  if (boss === null) return false;
  // Respect the global enemy cap so the boss can't push the world past
  // its budget. Mirrors the gate in `updateSpaceEnemySpawner`.
  if (spaceState.enemies.length >= SPACE_ENEMY_MAX_ACTIVE) return false;
  const drone = SPAWN_SPEC.fighter.acquire();
  if (drone === null) return false;
  // Place the drone fully to the left of the boss: subtract the drone's
  // own width so its right edge sits at `boss.x - X_OFFSET` rather than
  // overlapping the boss hitbox.
  const spawnX = boss.x - VOID_MOTHERSHIP_DRONE_SPAWN_X_OFFSET - drone.width;
  // Centre on the boss, jitter ± the configured range, then clamp to
  // the same Y window the regular spawner uses (read straight off
  // SPAWN_SPEC.fighter so any future tweak to fighter reach / inset —
  // e.g. wobble amplitude — flows through here without a second edit).
  const spec = SPAWN_SPEC.fighter;
  const centreY = boss.y + boss.height / 2 - drone.height / 2;
  const jitter = (droneRng() * 2 - 1) * VOID_MOTHERSHIP_DRONE_SPAWN_Y_JITTER;
  const yMin = SPACE_ENEMY_SPAWN_Y_MIN + spec.yMinInset;
  const yMax = VIEWPORT_HEIGHT - spec.verticalReach - SPACE_ENEMY_SPAWN_Y_MAX_OFFSET;
  const spawnY = Math.max(yMin, Math.min(yMax, centreY + jitter));
  drone.spawn(spawnX, spawnY);
  spaceState.enemies.push(drone);
  return true;
}
